import { useEffect, useRef, useState } from "react";
import { Spin, Card, Row, Col, Select, Input, Button, Space, App, Modal, Collapse } from "antd";
import { LoadingOutlined, SearchOutlined, UpOutlined, DownOutlined } from "@ant-design/icons";

const { Search } = Input;
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import { apiClient } from "../../services/api/apiClient";
import proj4 from "proj4";

// Estilos para ajustar z-index de controles de Leaflet
const leafletControlStyles = `
  .leaflet-top,
  .leaflet-bottom {
    z-index: 400 !important;
  }
  .leaflet-control {
    z-index: 400 !important;
  }
  .leaflet-pane {
    z-index: 400 !important;
  }
  .leaflet-popup-pane {
    z-index: 700 !important;
  }
`;

// Inyectar estilos
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.innerHTML = leafletControlStyles;
  if (!document.head.querySelector('style[data-leaflet-custom]')) {
    styleElement.setAttribute('data-leaflet-custom', 'true');
    document.head.appendChild(styleElement);
  }
}

// Función helper para generar el contenido del popup
const generatePopupContent = (
  props: any,
  t: (key: string) => string,
  iconColor: string = "#52c41a",
  icon: string = "🎯",
  showComparador: boolean = true
) => {
  const provinciasComunidadValenciana = [3, 12, 46];
  const esComunidadValenciana = provinciasComunidadValenciana.includes(props.provincia);
  
  let comparadorUrl = '';
  if (esComunidadValenciana && showComparador && props.bounds) {
    const { minX, minY, maxX, maxY } = props.bounds;
    comparadorUrl = `https://visor.gva.es/visor/index.html?extension=${minX},${minY},${maxX},${maxY}&nivelZoom=18&TipVisor=comparador&capasids=Orto_1956;,Orto_2025_Compara;&tcapas=1.0,1.0&nodoDesplegado=Orto_1956;&idioma=es`;
  }
  
  return `
    <div style="font-family: Arial, sans-serif; font-size: 13px;">
      <strong style="font-size: 14px; color: ${iconColor};">${icon} ${t('map.sigpac.recinto')}</strong><br/>
      <hr style="margin: 5px 0; border: none; border-top: 1px solid #ddd;"/>
      <strong>${t('map.sigpac.ubicacion')}:</strong><br/>
      ${props.provincia_nombre ? `${t('map.sigpac.provincia')}: ${props.provincia_nombre}<br/>` : props.provincia ? `${t('map.sigpac.provincia')}: ${props.provincia}<br/>` : ''}
      ${props.municipio_nombre ? `${t('map.sigpac.municipio')}: ${props.municipio_nombre}<br/>` : props.municipio ? `${t('map.sigpac.municipio')}: ${props.municipio}<br/>` : ''}
      ${props.poligono ? `${t('map.sigpac.poligono')}: ${props.poligono}<br/>` : ''}
      ${props.parcela ? `${t('map.sigpac.parcela')}: ${props.parcela}<br/>` : ''}
      ${props.recinto ? `${t('map.sigpac.recinto_num')}: ${props.recinto}<br/>` : ''}
      ${props.agregado !== undefined && props.agregado !== null ? `${t('map.sigpac.agregado')}: ${props.agregado}<br/>` : ''}
      ${props.zona !== undefined && props.zona !== null ? `${t('map.sigpac.zona')}: ${props.zona}<br/>` : ''}
      ${props.superficie ? `${t('map.sigpac.superficie')}: <strong>${props.superficie} ha</strong><br/>` : ''}
      ${props.uso_sigpac ? `<hr style="margin: 5px 0; border: none; border-top: 1px solid #ddd;"/><strong>${t('map.sigpac.caracteristicas')}:</strong><br/>${t('map.sigpac.uso')}: <strong>${props.uso_sigpac}</strong><br/>` : ''}
      ${esComunidadValenciana && comparadorUrl ? `
        <hr style="margin: 5px 0; border: none; border-top: 1px solid #ddd;"/>
        <strong>🔍 ${t('map.sigpac.herramientas')}:</strong><br/>
        <a href="${comparadorUrl}" target="_blank" rel="noopener noreferrer" style="color: #1890ff; text-decoration: none; font-weight: bold;">
          📸 ${t('map.sigpac.comparador_ortofotos')}
        </a>
      ` : ''}
    </div>
  `;
};

export interface CustomMapProps {
  // Configuración del mapa
  initialCenter?: [number, number];
  initialZoom?: number;
  height?: string;
  
  // Callbacks para eventos
  onMapReady?: (map: L.Map) => void;
  onRecintoClick?: (recintoData: any) => void;
  onRecintoSearch?: (recintoData: any) => void;
  
  // Opciones de visualización
  showLayerControl?: boolean;
  showSearchBar?: boolean;
  showAddressSearch?: boolean;
  
  // Datos externos
  mapData?: any; // GeoJSON FeatureCollection con datos de fincas/auditorías
  planningId?: string; // ID de la planificación para cargar parcelas dinámicamente
}

const CustomMap: React.FC<CustomMapProps> = ({
  initialCenter = [40.02, -4.9],
  initialZoom = 14,
  height = "600px",
  onMapReady,
  onRecintoClick,
  onRecintoSearch,
  showLayerControl = true,
  showSearchBar = true,
  showAddressSearch = true,
  mapData,
  planningId,
}) => {
  const { t } = useTranslation();
  // Ant 5: modal via useApp() para que participe del <App> context
  const { message, modal } = App.useApp();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const highlightLayerRef = useRef<L.GeoJSON | null>(null);
  const isCatastroActiveRef = useRef<boolean>(false); // Rastrear si la capa Catastro está activa
  const [loadingRecinto, setLoadingRecinto] = useState(false); // Estado para el spinner (compartido para click y búsqueda)
  
  // Refs para capas de datos
  const markerClusterGroupRef = useRef<any>(null);
  const plotsLayerRef = useRef<L.LayerGroup | null>(null);
  const [currentZoom, setCurrentZoom] = useState(initialZoom);
  const [loadingPlots, setLoadingPlots] = useState(false);
  
  // Estados para el buscador SIGPAC
  const [provincias, setProvincias] = useState<Array<{codigo: string, descripcion: string}>>([]);
  const [municipios, setMunicipios] = useState<Array<{codigo: string, descripcion: string}>>([]);
  const [provincia, setProvincia] = useState("");
  const [municipio, setMunicipio] = useState("");
  const [poligono, setPoligono] = useState("");
  const [parcela, setParcela] = useState("");
  const [recinto, setRecinto] = useState("");
  const [loadingProvincias, setLoadingProvincias] = useState(false);
  const [loadingMunicipios, setLoadingMunicipios] = useState(false);
  
  // Estados para el buscador de direcciones
  const [addressQuery, setAddressQuery] = useState("");
  const [loadingAddress, setLoadingAddress] = useState(false);
  
  // Estado para controlar el collapse de los buscadores
  const [searchPanelCollapsed, setSearchPanelCollapsed] = useState(false);
  
  // Memorizar callbacks para evitar re-renders
  const onRecintoClickRef = useRef(onRecintoClick);
  const onRecintoSearchRef = useRef(onRecintoSearch);
  const onMapReadyRef = useRef(onMapReady);
  
  useEffect(() => {
    onRecintoClickRef.current = onRecintoClick;
  }, [onRecintoClick]);
  
  useEffect(() => {
    onRecintoSearchRef.current = onRecintoSearch;
  }, [onRecintoSearch]);
  
  useEffect(() => {
    onMapReadyRef.current = onMapReady;
  }, [onMapReady]);
  
  // Memorizar initialCenter para evitar re-renders
  const centerKey = `${initialCenter[0]},${initialCenter[1]}`;
  const stableCenter = useRef(initialCenter);
  
  useEffect(() => {
    stableCenter.current = initialCenter;
  }, [centerKey]);
  
  // Cargar provincias al montar el componente (solo si showSearchBar es true)
  useEffect(() => {
    if (!showSearchBar) return;
    
    const fetchProvincias = async () => {
      setLoadingProvincias(true);
      try {
        const response = await fetch("https://sigpac-hubcloud.es/codigossigpac/provincia.json");
        const data = await response.json();
        setProvincias(data.codigos || []);
      } catch (error) {
        message.error(t('map.sigpac.error_provincias'));
      } finally {
        setLoadingProvincias(false);
      }
    };
    
    fetchProvincias();
  }, [showSearchBar, t]);
  
  // Cargar municipios cuando cambia la provincia
  useEffect(() => {
    if (!showSearchBar || !provincia) {
      setMunicipios([]);
      setMunicipio("");
      return;
    }
    
    const fetchMunicipios = async () => {
      setLoadingMunicipios(true);
      setMunicipios([]);
      setMunicipio("");
      
      try {
        const response = await fetch(`https://sigpac-hubcloud.es/codigossigpac/municipio${provincia}.json`);
        const data = await response.json();
        setMunicipios(data.codigos || []);
      } catch (error) {
        message.error(t('map.sigpac.error_municipios'));
      } finally {
        setLoadingMunicipios(false);
      }
    };
    
    fetchMunicipios();
  }, [provincia, showSearchBar, t]);
  
  // Inicializar mapa
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    
    const map = L.map(mapRef.current).setView(stableCenter.current, initialZoom);
    mapInstanceRef.current = map;
    
    // Definir capas base
    const arcgisSatellite = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      {
        maxZoom: 19,
        attribution: "&copy; Esri",
      }
    );
    
    const arcgisLabels = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      {
        maxZoom: 19,
        attribution: "&copy; Esri",
      }
    );
    
    // Crear grupo de capas para satélite con nombres
    const arcgisSatelliteWithLabels = L.layerGroup([arcgisSatellite, arcgisLabels]);
    
    const googleSatellite = L.tileLayer(
      "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
      {
        maxZoom: 20,
        attribution: "&copy; Google",
      }
    );
    
    const openStreetMap = L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap contributors",
      }
    );
    
    const ignBase = L.tileLayer(
      "https://tms-ign-base.idee.es/1.0.0/IGNBaseTodo/{z}/{x}/{-y}.jpeg",
      {
        maxZoom: 19,
        attribution: "&copy; IGN",
      }
    );
    
    // Objeto de capas base
    const baseLayers: { [key: string]: L.Layer } = {
      "Satélite (ArcGIS)": arcgisSatellite,
      "Satélite con Nombres (ArcGIS)": arcgisSatelliteWithLabels,
      "Satélite (Google)": googleSatellite,
      "OpenStreetMap": openStreetMap,
      "IGN Base": ignBase,
    };
    
    // Agregar capa base por defecto
    arcgisSatellite.addTo(map);
    
    // Capa de Catastro como overlay
    const catastroLayer = L.tileLayer.wms(
      "https://ovc.catastro.meh.es/Cartografia/WMS/ServidorWMS.aspx",
      {
        layers: "Catastro",
        format: "image/png",
        transparent: true,
        attribution: "&copy; Dirección General del Catastro",
      }
    );
    
    // Objeto de overlays
    const overlayLayers: { [key: string]: L.Layer } = {
      "Catastro": catastroLayer,
    };
    
    // Control de capas
    if (showLayerControl) {
      L.control.layers(baseLayers, overlayLayers, {
        position: 'topright',
        collapsed: true, // Mostrar como desplegable
      }).addTo(map);
      
      // Escuchar eventos de activación/desactivación de capas
      map.on('overlayadd', (e: any) => {
        if (e.name === 'Catastro') {
          isCatastroActiveRef.current = true;
        }
      });
      
      map.on('overlayremove', (e: any) => {
        if (e.name === 'Catastro') {
          isCatastroActiveRef.current = false;
        }
      });
    }
    
    // Click en el mapa para obtener recinto por coordenadas
    map.on("click", async (e: L.LeafletMouseEvent) => {
      // Solo funcionar si la capa de Catastro está activa
      if (!isCatastroActiveRef.current) {
        return;
      }
      
      const { lat, lng } = e.latlng;
      
      // Mostrar spinner
      setLoadingRecinto(true);
      
      try {
        const params = new URLSearchParams({
          srid: '4326',
          x: lng.toString(),
          y: lat.toString(),
        });
        
        const url = `/sigpac/recinto-by-point/?${params.toString()}`;
        const response = await apiClient.get(url);
        const geojsonData = response.data;
        
        if (geojsonData.features && geojsonData.features.length > 0) {
          if (highlightLayerRef.current) {
            map.removeLayer(highlightLayerRef.current);
            highlightLayerRef.current = null;
          }
          
          const feature = geojsonData.features[0];
          const props = feature.properties || {};
          
          const highlightLayer = L.geoJSON(feature, {
            style: {
              fillColor: "#00ff00",
              fillOpacity: 0.4,
              color: "#00aa00",
              weight: 3,
            },
          });
          
          highlightLayer.addTo(map);
          highlightLayerRef.current = highlightLayer;
          
          const bounds = highlightLayer.getBounds();
          const southWest = bounds.getSouthWest();
          const northEast = bounds.getNorthEast();
          
          proj4.defs('EPSG:25830', '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs');
          const wgs84 = 'EPSG:4326';
          const utm30n = 'EPSG:25830';
          
          const swUTM = proj4(wgs84, utm30n, [southWest.lng, southWest.lat]);
          const neUTM = proj4(wgs84, utm30n, [northEast.lng, northEast.lat]);
          
          const padding = 200;
          const minX = Math.round(swUTM[0] - padding);
          const minY = Math.round(swUTM[1] - padding);
          const maxX = Math.round(neUTM[0] + padding);
          const maxY = Math.round(neUTM[1] + padding);
          
          const provinciasComunidadValenciana = [3, 12, 46];
          const esComunidadValenciana = provinciasComunidadValenciana.includes(props.provincia);
          
          let comparadorUrl = '';
          if (esComunidadValenciana) {
            comparadorUrl = `https://visor.gva.es/visor/index.html?extension=${minX},${minY},${maxX},${maxY}&nivelZoom=18&TipVisor=comparador&capasids=Orto_1956;,Orto_2025_Compara;&tcapas=1.0,1.0&nodoDesplegado=Orto_1956;&idioma=es`;
          }
          
          // Agregar bounds al props para el comparador
          const propsWithBounds = {
            ...props,
            bounds: { minX, minY, maxX, maxY }
          };
          
          const popupContent = generatePopupContent(propsWithBounds, t, "#52c41a", "🎯", true);
          
          const center = bounds.getCenter();
          L.popup({
            maxWidth: 300,
            className: 'sigpac-popup'
          })
            .setLatLng(center)
            .setContent(popupContent)
            .openOn(map);
          
          // Callback personalizado
          if (onRecintoClickRef.current) {
            onRecintoClickRef.current(props);
          }
          
          message.success(t('map.sigpac.recinto_encontrado', {
            provincia: props.provincia,
            municipio: props.municipio,
            poligono: props.poligono,
            parcela: props.parcela,
            recinto: props.recinto
          }));
        }
      } catch (error: any) {
        if (error.response?.status !== 404) {
          // Error silencioso para 404 (no hay recinto en ese punto)
        }
      } finally {
        // Ocultar spinner
        setLoadingRecinto(false);
      }
    });
    
    // Llamar al callback onMapReady si está definido
    if (onMapReadyRef.current) {
      onMapReadyRef.current(map);
    }
    
    // Cleanup
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialZoom, showLayerControl]);
  
  // Función para buscar recinto SIGPAC
  const handleSearchRecinto = async () => {
    if (!provincia || !municipio) {
      message.warning(t('map.sigpac.seleccionar_provincia_municipio'));
      return;
    }
    
    if (!mapInstanceRef.current) {
      message.error(t('map.sigpac.mapa_no_inicializado'));
      return;
    }
    
    setLoadingRecinto(true);
    
    if (highlightLayerRef.current) {
      mapInstanceRef.current.removeLayer(highlightLayerRef.current);
      highlightLayerRef.current = null;
    }
    
    // Si no hay polígono, parcela o recinto, centrar en el municipio
    if (!poligono || !parcela || !recinto) {
      try {
        const provinciaNombre = provincias.find(p => p.codigo === provincia)?.descripcion || '';
        const municipioNombre = municipios.find(m => m.codigo === municipio)?.descripcion || '';
        
        const url = `/sigpac/municipality-center/?municipality_name=${encodeURIComponent(municipioNombre)}&province_name=${encodeURIComponent(provinciaNombre)}`;
        const response = await apiClient.get(url);
        const data = response.data;
        
        if (data.lat && data.lng) {
          if (mapInstanceRef.current) {
            mapInstanceRef.current.setView([data.lat, data.lng], 15);
          }
          
          message.success(t('map.sigpac.mapa_centrado', { municipio: municipioNombre }));
        }
      } catch (error) {
        message.error(t('map.sigpac.error_centrar_municipio'));
      } finally {
        setLoadingRecinto(false);
      }
      return;
    }
    
    // Buscar recinto específico
    try {
      const params = new URLSearchParams({
        provincia: String(provincia),
        municipio: String(municipio),
        poligono: String(poligono),
        parcela: String(parcela),
        recinto: String(recinto),
      });
      
      const url = `/sigpac/recinto-geometry/?${params.toString()}`;
      const response = await apiClient.get(url);
      const geojsonData = response.data;
      
      if (!geojsonData.features || geojsonData.features.length === 0) {
        modal.error({
          title: t('map.sigpac.recinto_no_encontrado'),
          content: `No se encontró el recinto SIGPAC con la referencia: ${provincia}/${municipio}/${poligono}/${parcela}/${recinto}`,
          okText: t('common.confirm'),
        });
        return;
      }
      
      const feature = geojsonData.features[0];
      const props = feature.properties || {};
      
      // Crear el layer con estilo de resaltado
      const highlightLayer = L.geoJSON(feature, {
        style: {
          fillColor: "#1890ff",
          fillOpacity: 0.3,
          color: "#1890ff",
          weight: 3,
        },
      });
      
      // Agregar al mapa
      highlightLayer.addTo(mapInstanceRef.current);
      highlightLayerRef.current = highlightLayer;
      
      // Calcular bounds para zoom y comparador GVA
      const bounds = highlightLayer.getBounds();
      const southWest = bounds.getSouthWest();
      const northEast = bounds.getNorthEast();
      
      proj4.defs('EPSG:25830', '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs');
      const wgs84 = 'EPSG:4326';
      const utm30n = 'EPSG:25830';
      
      const swUTM = proj4(wgs84, utm30n, [southWest.lng, southWest.lat]);
      const neUTM = proj4(wgs84, utm30n, [northEast.lng, northEast.lat]);
      
      const padding = 200;
      const minX = Math.round(swUTM[0] - padding);
      const minY = Math.round(swUTM[1] - padding);
      const maxX = Math.round(neUTM[0] + padding);
      const maxY = Math.round(neUTM[1] + padding);
      
      // Obtener nombres de provincia y municipio desde los selectores
      const provinciaNombre = provincias.find(p => p.codigo === provincia)?.descripcion || provincia;
      const municipioNombre = municipios.find(m => m.codigo === municipio)?.descripcion || municipio;
      
      // Agregar bounds y nombres al props para el comparador
      const propsWithBounds = {
        ...props,
        provincia_nombre: provinciaNombre,
        municipio_nombre: municipioNombre,
        bounds: { minX, minY, maxX, maxY }
      };
      
      // Generar contenido del popup
      const popupContent = generatePopupContent(propsWithBounds, t, "#1890ff", "📍", true);
      
      // Hacer zoom al recinto
      mapInstanceRef.current.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 17,
      });
      
      // Mostrar popup en el centro del recinto
      const center = bounds.getCenter();
      L.popup({
        maxWidth: 300,
        className: 'sigpac-popup'
      })
        .setLatLng(center)
        .setContent(popupContent)
        .openOn(mapInstanceRef.current);
      
      // Callback personalizado
      if (onRecintoSearchRef.current) {
        onRecintoSearchRef.current(feature.properties);
      }
      
      message.success(t('map.sigpac.recinto_encontrado_en', {
        municipio: municipioNombre,
        referencia: `${poligono}-${parcela}-${recinto}`
      }));
      
    } catch (error: any) {
      // Si es un 404, mostrar error específico de recinto no encontrado
      if (error.status === 404 || error.response?.status === 404) {
        message.error(`${t('map.sigpac.recinto_no_encontrado')}: ${provincia}/${municipio}/${poligono}/${parcela}/${recinto}`);
      } else {
        // Otros errores
        message.error(t('map.sigpac.error_buscar_recinto'));
      }
    } finally {
      setLoadingRecinto(false);
    }
  };
  
  // Función para buscar dirección
  const handleAddressSearch = async () => {
    if (!addressQuery.trim()) {
      message.warning("Por favor, ingrese una dirección o coordenadas");
      return;
    }
    
    if (!mapInstanceRef.current) {
      message.error(t('map.sigpac.mapa_no_inicializado'));
      return;
    }
    
    setLoadingAddress(true);
    
    try {
      // Verificar si es una búsqueda por coordenadas (formato: lat, lng)
      const coordsMatch = addressQuery.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
      
      if (coordsMatch) {
        // Búsqueda por coordenadas
        const lat = parseFloat(coordsMatch[1]);
        const lng = parseFloat(coordsMatch[2]);
        
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
          message.error("Coordenadas fuera de rango válido");
          setLoadingAddress(false);
          return;
        }
        
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setView([lat, lng], 15);
          
          // Agregar un marcador temporal
          const marker = L.marker([lat, lng]).addTo(mapInstanceRef.current);
          marker.bindPopup(`<strong>📍 Coordenadas</strong><br/>Lat: ${lat}<br/>Lng: ${lng}`).openPopup();
          
          // Eliminar el marcador después de 5 segundos
          setTimeout(() => {
            marker.remove();
          }, 5000);
          
          message.success(`Mapa centrado en: ${lat}, ${lng}`);
        }
        
        setLoadingAddress(false);
        return;
      }
      
      // Búsqueda por dirección usando Nominatim
      const nominatim_url = "https://nominatim.openstreetmap.org/search";
      const params = new URLSearchParams({
        q: addressQuery,
        format: 'json',
        limit: '1',
        addressdetails: '1'
      });
      
      const response = await fetch(`${nominatim_url}?${params.toString()}`, {
        headers: {
          'User-Agent': 'CyberAgropolis/1.0'
        }
      });
      
      if (response.ok) {
        const results = await response.json();
        
        if (results && results.length > 0) {
          const result = results[0];
          const lat = parseFloat(result.lat);
          const lng = parseFloat(result.lon);
          
          if (mapInstanceRef.current) {
            mapInstanceRef.current.setView([lat, lng], 15);
            
            // Agregar un marcador temporal
            const marker = L.marker([lat, lng]).addTo(mapInstanceRef.current);
            marker.bindPopup(`<strong>${result.display_name}</strong>`).openPopup();
            
            // Eliminar el marcador después de 5 segundos
            setTimeout(() => {
              marker.remove();
            }, 5000);
            
            message.success(`Ubicación encontrada: ${result.display_name}`);
          }
        } else {
          message.error("No se encontró la dirección. Intente con otra búsqueda.");
        }
      } else {
        message.error("Error al buscar la dirección");
      }
    } catch (error) {
      console.error("Error en búsqueda de dirección:", error);
      message.error("Error al buscar la dirección");
    } finally {
      setLoadingAddress(false);
    }
  };
  
  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      {/* Mapa con buscadores integrados */}
      <div style={{ position: "relative", width: "100%" }}>
        <div
          ref={mapRef}
          style={{ height, width: "100%", borderRadius: "4px", overflow: "hidden" }}
        />
        
        {/* Panel de buscadores colapsable dentro del mapa */}
        {(showAddressSearch || showSearchBar) && (
          <div
            style={{
              position: "absolute",
              top: "10px",
              left: "60px", // Desplazado a la derecha para no tapar los controles de zoom
              right: "10px",
              zIndex: 1000,
              maxWidth: "800px",
            }}
          >
            <Card
              size="small"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(4px)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
              bodyStyle={{ padding: searchPanelCollapsed ? "8px" : "12px" }}
            >
              {/* Botón de collapse */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: searchPanelCollapsed ? 0 : 8 }}>
                <strong style={{ fontSize: "14px" }}>
                  🔍 {showSearchBar ? "Buscadores" : "Buscar Ubicación"}
                </strong>
                <Button
                  type="text"
                  size="small"
                  icon={searchPanelCollapsed ? <DownOutlined /> : <UpOutlined />}
                  onClick={() => setSearchPanelCollapsed(!searchPanelCollapsed)}
                />
              </div>
              
              {/* Contenido colapsable */}
              {!searchPanelCollapsed && (
                <Row gutter={[16, 16]}>
                  {/* Buscador de Direcciones */}
                  {showAddressSearch && (
                    <Col xs={24} lg={showSearchBar ? 12 : 24}>
                      <div style={{ fontSize: "12px", fontWeight: 500, marginBottom: 8 }}>
                        📍 Buscar Dirección o Coordenadas
                      </div>
                      <Search
                        placeholder="Dirección: Calle Mayor 1, Madrid | Coordenadas: 40.4168, -3.7038"
                        value={addressQuery}
                        onChange={(e) => setAddressQuery(e.target.value)}
                        onSearch={handleAddressSearch}
                        enterButton={<SearchOutlined />}
                        loading={loadingAddress}
                        size="small"
                      />
                    </Col>
                  )}
                  
                  {/* Buscador SIGPAC */}
                  {showSearchBar && (
                    <Col xs={24} lg={showAddressSearch ? 12 : 24}>
                      <div style={{ fontSize: "12px", fontWeight: 500, marginBottom: 8 }}>
                        🌾 {t('map.sigpac.buscar_recinto')}
                      </div>
                      {/* Primera fila: Provincia y Municipio */}
                      <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
                        <Col xs={12}>
                          <Select
                            showSearch
                            placeholder={t('map.sigpac.provincia_placeholder')}
                            value={provincia || undefined}
                            onChange={(value) => setProvincia(value)}
                            style={{ width: "100%" }}
                            loading={loadingProvincias}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            options={provincias.map((p) => ({
                              value: p.codigo,
                              label: `${p.codigo} - ${p.descripcion}`,
                            }))}
                            allowClear
                            size="small"
                          />
                        </Col>
                        <Col xs={12}>
                          <Select
                            showSearch
                            placeholder={t('map.sigpac.municipio_placeholder')}
                            value={municipio || undefined}
                            onChange={(value) => setMunicipio(value)}
                            style={{ width: "100%" }}
                            disabled={!provincia || loadingMunicipios}
                            loading={loadingMunicipios}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            options={municipios.map((m) => ({
                              value: m.codigo,
                              label: `${m.codigo} - ${m.descripcion}`,
                            }))}
                            allowClear
                            size="small"
                          />
                        </Col>
                      </Row>
                      {/* Segunda fila: Polígono, Parcela, Recinto y Botón */}
                      <Row gutter={[8, 8]}>
                        <Col xs={6}>
                          <Input
                            placeholder={t('map.sigpac.poligono_placeholder')}
                            value={poligono}
                            onChange={(e) => setPoligono(e.target.value.replace(/\D/g, ""))}
                            maxLength={5}
                            size="small"
                          />
                        </Col>
                        <Col xs={6}>
                          <Input
                            placeholder={t('map.sigpac.parcela_placeholder')}
                            value={parcela}
                            onChange={(e) => setParcela(e.target.value.replace(/\D/g, ""))}
                            maxLength={5}
                            size="small"
                          />
                        </Col>
                        <Col xs={6}>
                          <Input
                            placeholder={t('map.sigpac.recinto_placeholder')}
                            value={recinto}
                            onChange={(e) => setRecinto(e.target.value.replace(/\D/g, ""))}
                            maxLength={4}
                            size="small"
                          />
                        </Col>
                        <Col xs={6}>
                          <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearchRecinto}
                            disabled={!provincia || !municipio}
                            size="small"
                            style={{ width: "100%" }}
                          />
                        </Col>
                      </Row>
                    </Col>
                  )}
                </Row>
              )}
            </Card>
          </div>
        )}
        
        {/* Spinner de carga de recinto */}
        {loadingRecinto && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              padding: "24px 32px",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 32, color: "#52c41a" }} spin />}
              size="large"
            />
            <div style={{ fontSize: "16px", fontWeight: 500, color: "#52c41a" }}>
              {t('map.sigpac.cargando_recinto')}
            </div>
          </div>
        )}
      </div>
    </Space>
  );
};

export default CustomMap;
